import { Typography, Box } from '@mui/material';
import Grid from 'components/core/Grid';
import { LoadingIndicator } from 'components/core/LoadingIndicator/LoadingIndicator';
import { listMembershipStandardImage } from 'config/imageUrls';
import {
  SubscriptionTierID,
  useRestauthSubscriptionTiersQuery,
} from 'hooks/api/property/queries/useSubscriptionTiersQuery';
import React from 'react';
import { ListPageAnchorEnum } from '../types';

import { ContentSection, StyledBulletList, MembershipPricingDetails, StyledAppCard } from './styles';

const MembershipItem = ({
  name,
  title,
  description,
  points,
  similarSite,
  annualFee,
  bookingFee,
  creditCardFee,
}: {
  name: string;
  title: string;
  description: string;
  points: string[];
  similarSite: string;
  annualFee: string;
  bookingFee: string;
  creditCardFee: string;
}) => {
  return (
    <StyledAppCard className="noPadding">
      <Box p={3}>
        <Box display="flex" alignItems="center" mb={3}>
          <img src={listMembershipStandardImage} style={{ width: '40px', marginRight: '16px' }} />
          <Typography variant="h3">{name}</Typography>
        </Box>
        <Typography>
          <strong>{title}</strong>
        </Typography>
        <Typography>{description}</Typography>
        <StyledBulletList style={{ minHeight: '104px' }}>
          {points.map(point => (
            <li key={point}>{point}</li>
          ))}
        </StyledBulletList>
        <Typography variant="caption">*Similar to {similarSite} listings</Typography>
      </Box>

      <MembershipPricingDetails container spacing={2}>
        <Grid item xs={4}>
          <Typography>
            <strong>Annual Fee</strong>
          </Typography>
          <Typography>${annualFee} CAD</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography>
            <strong>Booking Fee</strong>
          </Typography>
          <Typography>{bookingFee}%</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography>
            <strong>Credit Card Fee</strong>
          </Typography>
          <Typography>{creditCardFee}%</Typography>
        </Grid>
      </MembershipPricingDetails>
    </StyledAppCard>
  );
};

const membershipData = [
  {
    id: SubscriptionTierID.Standard,
    title: 'Pay For Performance',
    description:
      'Pay only for confirmed reservations. Best for rentals that are new to the marketplace or looking to diversify booking platforms.',
    points: [
      'No upfront costs or risk. Get listed for $0.00',
      'Same features and marketing as Premium',
      'Booking fees only apply to confirmed bookings',
    ],
    similarSite: 'VRBO free',
    annualFee: '0.00',
  },
  /*
  {
    id: SubscriptionTierID.Guest,
    title: 'Guest Pays More',
    description:
      'The Host booking fee is passed onto the guest as an additional surcharge. This option reduces the overall performance of the listing.',
    points: ['No upfront costs or risk. Get listed for $0.00', 'Same features and marketing as Premium'],
    similarSite: 'airBNB',
    annualFee: '0.00',
  },
  */
  {
    id: SubscriptionTierID.Premium,
    title: 'Cost-Effective Option',
    description:
      'Our brand & marketing performs very well in specific locations. For these properties, removing the booking fee is cost-effective.',
    points: ['Removes the reservation booking fee', 'Purchase provides 365 days of coverage'],
    similarSite: 'VRBO paid',
    annualFee: '795.00',
  },
];

export const MembershipPricing = () => {
  const subscriptionTiers = useRestauthSubscriptionTiersQuery();

  if (!subscriptionTiers.data) return <LoadingIndicator />;

  const {
    data: {
      DATA: { SUBSCRIPTION_TIERS },
    },
  } = subscriptionTiers;
  const computedMembershipData = membershipData.map(item => {
    const { id } = item;
    const subscriptionTier = SUBSCRIPTION_TIERS.find(result => result.ID === id);
    return {
      ...item,
      bookingFee: String(subscriptionTier?.BOOKING_FEE) ?? '',
      creditCardFee: String(subscriptionTier?.CREDIT_CARD_FEE) ?? '',
      name: subscriptionTier?.TITLE ?? '',
    };
  });

  return (
    <div id={ListPageAnchorEnum.pricing}>
      <ContentSection>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h2" mb={2}>
              Membership Pricing
            </Typography>
            <Typography>
              If you have a vacation rental property located at a mountain resort, listing on alluraDirect has no risk
              and helps build the community. There are three membership options to choose from - if you are new to
              alluraDirect, start with the &quot;Host Commission&quot; to allow us to show you what we can do.
            </Typography>
          </Grid>
          {computedMembershipData.map(item => (
            <Grid item xs={12} md={4} key={item.id}>
              <MembershipItem {...item} />
            </Grid>
          ))}
        </Grid>
      </ContentSection>
    </div>
  );
};
